import './manager-stage-navigation.scss'

import {
  Box,
  Step,
  StepButton,
  StepConnector,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
  stepLabelClasses,
} from '@mui/material'
import {
  Check,
  HourglassTop,
  Work,
  Remove,
} from '@vedmarket/ui-kit'
import {
  useTranslation,
} from 'react-i18next'

import {
  COLORS,
  MANAGER_WORKFLOW_STEP_STATE,
} from 'config/constants'
import {
  MANAGER_WORKFLOW_STEP_TITLE_KEYS,
  managerWorkflowStepStateLabelKey,
} from '../manager-workflow'

const ManagerStageNavigation = ({
  steps,
  app,
  selectedStepId,
  onSelectStep,
}: ApplicationManagerStageNavigationProps) => {
  const { t } = useTranslation()

  const currentStepIndex = steps.findIndex((step) => (
    step.state === MANAGER_WORKFLOW_STEP_STATE.CURRENT
  ))

  return (
    <Box
      component='nav'
      className='application-manager-stage-navigation'
      aria-label={t('applications__text__mgrWorkflowTitle')}
    >
      <Stepper
        nonLinear
        activeStep={currentStepIndex}
        orientation='vertical'
        connector={null}
        sx={{ alignItems: 'stretch' }}
      >
        {steps.map((step, index) => {
          const isSelected = selectedStepId === step.id
          const isCompleted = step.state === MANAGER_WORKFLOW_STEP_STATE.COMPLETED
          const isCurrent = step.state === MANAGER_WORKFLOW_STEP_STATE.CURRENT
          const isSkipped = step.state === MANAGER_WORKFLOW_STEP_STATE.SKIPPED
          const title = t(MANAGER_WORKFLOW_STEP_TITLE_KEYS[step.id])
          const stateLabelKey = managerWorkflowStepStateLabelKey({ step, app })
          const stateLabel = t(stateLabelKey)
          const hasAdditionalInfo = isSkipped || stateLabelKey !== `applications__text__mgrStep_state_${step.state}`
          const isFinished = isCompleted || isSkipped
          const isAvailable = isFinished || isCurrent
          const isSelectable = isCompleted || isCurrent
          const isPending = !isAvailable
          let markerColor = 'action.disabled'
          if (isCompleted) {
            markerColor = COLORS.GREEN
          } else if (isSkipped) {
            markerColor = 'text.secondary'
          }

          const statusIcon = (
            <Tooltip title={stateLabel}>
              <Box
                role='img'
                className='application-manager-stage-navigation__marker'
                aria-label={stateLabel}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  color: isCompleted ? COLORS.WHITE : markerColor,
                  bgcolor: isCompleted ? COLORS.GREEN : undefined,
                  borderRadius: isCompleted ? '50%' : undefined,
                  boxSizing: 'border-box',
                  flexShrink: 0,
                }}
              >
                {isCompleted && <Check width={20} height={20} />}
                {isSkipped && <Remove width={20} height={20} />}
                {isPending && <HourglassTop width={20} height={20} />}
                {isCurrent && <Work width={20} height={20} />}
              </Box>
            </Tooltip>
          )

          return (
            <Step
              key={step.id}
              completed={isCompleted}
              active={isAvailable}
              disabled={!isSelectable}
              sx={{
                px: 0,
                minWidth: 0,
                position: 'relative',
                pb: index < steps.length - 1 ? '40px' : 0,
              }}
            >
              <StepButton
                icon={statusIcon}
                id={`application-manager-stage-button-${step.id}`}
                aria-controls={`application-manager-stage-${step.id}`}
                aria-pressed={isSelected}
                aria-current={isCurrent ? 'step' : undefined}
                onClick={() => onSelectStep(step.id)}
                optional={hasAdditionalInfo && <Typography component='span' variant='caption' sx={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '15.6px',
                  letterSpacing: 0,
                  color: 'text.primary',
                }}>
                  {stateLabel}
                </Typography>}
                sx={{
                  p: 0,
                  minHeight: '24px',
                  m: 0,
                  justifyContent: 'flex-start',
                  boxSizing: 'border-box',
                }}
              >
                <StepLabel
                  sx={{
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    py: 0,
                    gap: '8px',
                    [`& .${stepLabelClasses.iconContainer}`]: {
                      pr: 0,
                      width: '24px',
                      justifyContent: 'center',
                    },
                  }}
                  slotProps={{
                    label: {
                      sx: {
                        fontSize: '14px',
                        lineHeight: '21px',
                        letterSpacing: 0,
                        textAlign: 'left',
                        color: isAvailable ? 'text.primary' : 'action.disabled',
                        fontWeight: isAvailable ? 500 : 400,
                      },
                    },
                  }}
                >
                  <Typography
                    component='span'
                    variant='inherit'
                    color='inherit'
                    className='application-manager-stage-navigation__label'
                  >
                    {step.index}. {title}
                  </Typography>
                </StepLabel>
              </StepButton>
              {index < steps.length - 1 && <StepConnector sx={{
                position: 'absolute',
                top: '32px',
                bottom: '8px',
                left: 0,
                ml: 0,
                width: '24px',
                '& .MuiStepConnector-line': {
                  ml: '11px',
                  width: '1px',
                  height: '100%',
                  minHeight: 0,
                  borderColor: COLORS.STRONG_GRAY,
                  borderLeftWidth: '1px',
                },
              }} />}
            </Step>
          )
        })}
      </Stepper>
    </Box>
  )
}

export default ManagerStageNavigation
